import React, { useEffect, useState } from 'react';
import { Text, Group, Card, FileInput, Box } from '@mantine/core';
import { useTranslations } from 'next-intl';

interface InsuranceProvider {
  name: string;
  image: string;
}

interface PaymentStepProps {
  insuranceDetails: string;
  onInsuranceDetailsChange: (details: string) => void;
  onPaymentMethodChange: (method: string) => void;
  onEmiratesIdFileChange: (file: File | null) => void;
  onInsuranceCardFileChange: (file: File | null) => void;
  insuranceProviders: InsuranceProvider[];
  cardStyles?: React.CSSProperties;
}

const PaymentStep: React.FC<PaymentStepProps> = ({
  insuranceDetails,
  onInsuranceDetailsChange,
  onPaymentMethodChange,
  onEmiratesIdFileChange,
  onInsuranceCardFileChange,
  insuranceProviders,
  cardStyles = {},
}) => {
  const t = useTranslations('paymentStep');
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [hasInsurance, setHasInsurance] = useState(true);
  const [emiratesIdFile, setEmiratesIdFile] = useState<File | null>(null);
  const [insuranceCardFile, setInsuranceCardFile] = useState<File | null>(null);

  useEffect(() => {
    try {
      const details = JSON.parse(insuranceDetails || '{}');
      setSelectedProvider(details.provider || null);
      setHasInsurance(details.hasInsurance !== false);
    } catch (error) {
      setSelectedProvider(null);
      setHasInsurance(true);
    }
  }, [insuranceDetails]);

  useEffect(() => {
    const details = {
      provider: selectedProvider,
      hasInsurance,
    };

    onInsuranceDetailsChange(JSON.stringify(details));
    onPaymentMethodChange(hasInsurance ? 'insurance' : 'self-pay');
  }, [hasInsurance, onInsuranceDetailsChange, onPaymentMethodChange, selectedProvider]);

  const handleProviderSelect = (provider: string) => {
    setSelectedProvider(provider);
    setHasInsurance(true);
  };

  const handleNoInsurance = () => {
    setSelectedProvider(null);
    setHasInsurance(false);
    setInsuranceCardFile(null);
    onInsuranceCardFileChange(null);
  };

  const renderInsuranceOption = (
    name: string,
    imageSrc: string,
    isSelected: boolean,
    onClick: () => void
  ) => (
    <Card
      shadow="sm"
      p="lg"
      style={{
        ...cardStyles,
        backgroundColor: isSelected ? '#f0f0f0' : 'white',
        width: '150px',
        height: '200px',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <img
        src={imageSrc}
        alt={t(name)}
        style={{ width: '100%', height: '120px', objectFit: 'contain' }}
      />
      <Text style={{ textAlign: 'center' }}>{t(name)}</Text>
    </Card>
  );

  return (
    <>
      <Text>{t('selectInsuranceProvider')}</Text>
      <Group mt="md">
        {insuranceProviders.map((provider) =>
          renderInsuranceOption(
            provider.name,
            provider.image,
            selectedProvider === provider.name,
            () => handleProviderSelect(provider.name)
          )
        )}
        <Card
          shadow="sm"
          p="lg"
          style={{
            ...cardStyles,
            backgroundColor: !hasInsurance ? '#f0f0f0' : 'white',
            width: '150px',
            height: '200px',
            cursor: 'pointer',
          }}
          onClick={handleNoInsurance}
        >
          <img
            src="/images/selfpay.jpeg"
            alt={t('noInsurance')}
            style={{ width: '100%', height: '120px', objectFit: 'contain' }}
          />
          <Text style={{ textAlign: 'center' }}>{t('noInsurance')}</Text>
        </Card>
      </Group>

      <Box mt="xl">
        <FileInput
          label={t('uploadEmiratesIdOrPassport')}
          placeholder={t('chooseFile')}
          value={emiratesIdFile}
          onChange={(file) => {
            setEmiratesIdFile(file);
            onEmiratesIdFileChange(file);
          }}
          accept=".pdf,.jpeg,.jpg,.png"
          required
        />
      </Box>

      {hasInsurance && (
        <Box mt="md">
          <FileInput
            label={t('uploadInsuranceCard')}
            placeholder={t('chooseFile')}
            value={insuranceCardFile}
            onChange={(file) => {
              setInsuranceCardFile(file);
              onInsuranceCardFileChange(file);
            }}
            accept=".pdf,.jpeg,.jpg,.png"
          />
        </Box>
      )}
    </>
  );
};

export default PaymentStep;
